import { useState, useEffect } from "react";
import { X, Calendar, Clock } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import Swal from "sweetalert2";
import { 
  obtenerTodosDiasUtilizacion, 
  insertarHorarioZonaComun,
  DiaUtilizacion 
} from "../../../../api/zonaComunHorarioService";

interface CreateDiaHabilModalProps {
  isOpen: boolean;
  zonaComunCodigo: string;
  onClose: (created: boolean) => void;
}

export default function CreateDiaHabilModal({ 
  isOpen, 
  zonaComunCodigo,
  onClose 
}: CreateDiaHabilModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDia, setSelectedDia] = useState("");
  const [horaApertura, setHoraApertura] = useState("");
  const [horaCierre, setHoraCierre] = useState("");
  const [diasUtilizacion, setDiasUtilizacion] = useState<DiaUtilizacion[]>([]);
  const [isLoadingDias, setIsLoadingDias] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDiasUtilizacion();
      // Resetear formulario
      setSelectedDia("");
      setHoraApertura("");
      setHoraCierre("");
    }
  }, [isOpen]);

  const loadDiasUtilizacion = async () => {
    try {
      setIsLoadingDias(true);
      const response = await obtenerTodosDiasUtilizacion();
      
      if (response.code === "1" && response.object_) {
        setDiasUtilizacion(response.object_);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los días de utilización",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      }
    } catch (error) {
      console.error("Error al cargar días de utilización:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los días de utilización",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoadingDias(false);
    }
  };

  const validateTimeFormat = (time: string): boolean => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDia) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione un día",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!horaApertura) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de apertura",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!validateTimeFormat(horaApertura)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de apertura debe estar en formato militar (HH:MM). Ejemplo: 09:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!horaCierre) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de cierre",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!validateTimeFormat(horaCierre)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de cierre debe estar en formato militar (HH:MM). Ejemplo: 19:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    // Validar que la hora de cierre sea posterior a la hora de apertura
    const [aperturaHora, aperturaMinuto] = horaApertura.split(':').map(Number);
    const [cierreHora, cierreMinuto] = horaCierre.split(':').map(Number);
    const aperturaTotal = aperturaHora * 60 + aperturaMinuto;
    const cierreTotal = cierreHora * 60 + cierreMinuto;

    if (cierreTotal <= aperturaTotal) {
      Swal.fire({
        icon: "warning",
        title: "Horario inválido",
        text: "La hora de cierre debe ser posterior a la hora de apertura",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        "zona comun codigo": zonaComunCodigo,
        "dia de utilizacion codigo": selectedDia,
        "hora apertura": horaApertura,
        "hora cierre": horaCierre,
      };

      const response = await insertarHorarioZonaComun(payload);

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Día hábil agregado!",
          text: response.message || "El día hábil se ha agregado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al agregar el día hábil");
      }
    } catch (error) {
      console.error("Error al crear día hábil:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al agregar el día hábil";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al agregar el día hábil. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={28} />
            Agregar Día Hábil Zona Común
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Calendar size={16} className="inline mr-2 text-military-600" />
              Día de la Semana *
            </label>
            <select
              value={selectedDia}
              onChange={(e) => setSelectedDia(e.target.value)}
              disabled={isSubmitting || isLoadingDias}
              className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-3 bg-white shadow-sm"
              required
            >
              {isLoadingDias ? (
                <option value="">Cargando días...</option>
              ) : (
                <>
                  <option value="">Seleccionar...</option>
                  {diasUtilizacion.map((dia) => (
                    <option key={dia.ddu_codigo} value={dia.ddu_codigo}>
                      {dia.ddu_nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Clock size={16} className="inline mr-2 text-military-600" />
              Hora de Apertura (Formato Militar) *
            </label>
            <Input
              type="text"
              value={horaApertura}
              onChange={(e) => {
                const value = e.target.value;
                // Permitir solo números y dos puntos
                if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                  // Auto-formatear con dos puntos
                  if (value.length === 2 && !value.includes(':')) {
                    setHoraApertura(value + ':');
                  } else {
                    setHoraApertura(value);
                  }
                }
              }}
              placeholder="09:00"
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm"
              maxLength={5}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: HH:MM (24 horas). Ejemplo: 09:00, 14:30
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Clock size={16} className="inline mr-2 text-military-600" />
              Hora de Cierre (Formato Militar) *
            </label>
            <Input
              type="text"
              value={horaCierre}
              onChange={(e) => {
                const value = e.target.value;
                // Permitir solo números y dos puntos
                if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                  // Auto-formatear con dos puntos
                  if (value.length === 2 && !value.includes(':')) {
                    setHoraCierre(value + ':');
                  } else {
                    setHoraCierre(value);
                  }
                }
              }}
              placeholder="19:00"
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm"
              maxLength={5}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: HH:MM (24 horas). Ejemplo: 19:00, 22:30
            </p>
          </div>

          <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 -mx-6 px-6 pb-6">
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
                className="flex-1 border-2 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Calendar size={20} className="mr-2" />
                    Guardar Día Hábil
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

